<template>
  <div ref="targetEl">
    <ButtonTransition :buttonId="buttonId">
      <div style="height: 100%; position: relative;">
        <LinkWrapper
          :href="url"
          target="_blank"
          :disabled="disabled"
          :class="classes.wrapper"
          @click="handleButtonClick"
        >
          <div v-if="badgeOrJersey" :class="classes.badgeHolder">
            <TeamBadge
              v-if="showTeamBadge"
              :src="competitor && competitor.badge"
              :countryCode="competitor && competitor.colors?.country_code_a2"
              :class="classes.badge"
              :hideBoxShadow="true"
              :defaultBadge="homeOrAwayTeam"
            />

            <TeamJersey
              v-if="showTeamJerseys && teamIconsAvailable"
              :colors="competitor?.colors"
              :homeOrAwayTeam="homeOrAwayTeam"
              :class="classes.badge"
              :transform="isLeft ? 'scale(-1,1)' : undefined"
            />
          </div>

          <div :class="classes.button">
            <StyledText
              tag="span"
              :fontId="primaryFont"
              :class="[classes.text, classes.fadeOut, 'button-text']"
              :overrideColor="textColor"
            >
              {{ competitorName || label }}
            </StyledText>
            <div :class="[classes.oddsText, classes.fadeIn]">
              <StyledText
                v-if="meta"
                tag="span"
                :fontId="secondaryFont"
                :class="[classes.metaText, 'meta-text']"
                :overrideColor="textColor"
              >
                {{ meta }}
              </StyledText>
              <StyledText
                v-if="showWinnings"
                tag="span"
                :fontId="primaryFont"
                :class="[classes.odds, 'odds-text']"
                :overrideColor="textColor"
              >
                {{ winnings }}
              </StyledText>
              <StyledText
                v-else
                tag="span"
                :fontId="primaryFont"
                :class="[classes.odds, 'button-text']"
                :overrideColor="textColor"
              >
                {{ odds }}
              </StyledText>
            </div>
          </div>
        </LinkWrapper>
      </div>
    </ButtonTransition>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType, ComputedRef, provide } from "vue";
import TeamBadge from "./TeamBadge.vue";
import TeamJersey from "./team-jerseys/TeamJerseySelector.vue";
import StyledText from "./styled/Text.vue";
import LinkWrapper from "./LinkWrapper.vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import {
  injectBrand,
  BrandContext
} from "@fresh8/ad-data-composition/src/brand";
import { injectCardNumber, useAnalytics } from "../hooks/useAnalytics";
import { composeBetOption } from "@fresh8/ad-data-composition/src/betData";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import { getTranslation } from "@fresh8/ad-data-composition/src/lang";
import { formatOdds } from "@fresh8/ad-data-composition/src/lib/formatOdds";
import { SportCompetitor, Option } from "@fresh8/types/src/betData";
import usePotentialWinnings, {
  useShowWinnings
} from "@fresh8/ad-data-composition/src/usePotentialWinnings";
import ButtonTransition from "./ButtonTransition.vue";
import { injectTeamColors, injectUseTeamBadges } from "../lib/teamBranding";
import { injectUseTeamJerseys } from "../lib/teamJerseys";
import { oddsTypeEnum } from "@fresh8/types/src";
import { injectCarouselContext } from "../lib/provideInjectors";
import { injectTemplateSize } from "@fresh8/ad-data-composition/src/useTemplateSize";

const useStyles = createUseStyles({
  wrapper: {
    "&:hover": {
      cursor: "pointer"
    }
  },
  button: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    marginLeft: props => {
      if (props.alignment === "left" && props.badgesOrJersey) {
        return "14px";
      }

      return "";
    },
    marginRight: props => {
      if (props.alignment === "right" && props.badgesOrJersey) {
        return "14px";
      }

      return "";
    },
    background: props => props.buttonBackground,
    borderRadius: props => props.borderRadius,
    paddingRight: props => {
      if (props.alignment === "right" && props.badgesOrJersey) return "18px";

      return "";
    },
    paddingLeft: props => {
      if (props.alignment === "left" && props.badgesOrJersey) return "18px";

      return "";
    }
  },
  oddsText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "absolute",
    animation: "$fadeInOut 3s linear 0.5s infinite",
    animationFillMode: "both",
    width: props => `${props.textWidth}px`
  },
  odds: {
    fontSize: "16px",
    lineHeight: 1.2,
    textAlign: "center"
  },
  text: {
    fontSize: "10px",
    textAlign: "center",
    animation: "$fadeInOut 3s linear 0.5s infinite",
    animationFillMode: "both",
    position: "absolute",
    height: "34px",
    width: props => `${props.textWidth}px`,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center"
  },
  metaText: {
    fontSize: "8px",
    lineHeight: 1.2,
    textAlign: "center"
  },
  badgeHolder: {
    boxSizing: "border-box",
    position: "absolute",
    background: props => props.buttonBackground,
    right: props => (props.alignment === "right" ? `${0}px` : ""),
    left: props =>
      props.alignment === "left" ? `${props.leftBagdeAlignment}px` : "",
    height: "100%",
    border: props => `2px solid ${props.buttonBackground}`,

    borderRadius: "9001px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  badge: {
    borderWidth: props => props.badgeBorderWidth,
    maxWidth: "28px !important",

    "&.team-jersey": {
      width: "100%",
      height: "100%"
    }
  },
  fadeIn: {
    animationDirection: "alternate"
  },
  fadeOut: {
    animationDirection: "alternate-reverse"
  },
  "@keyframes fadeInOut": {
    "0%": { opacity: 0 },
    "50%": { opacity: 0 },
    "60%": { opacity: 1 },
    "100%": { opacity: 1 }
  }
});

export default defineComponent({
  name: "WinOption",
  components: {
    TeamBadge,
    TeamJersey,
    StyledText,
    LinkWrapper,
    ButtonTransition
  },
  props: {
    optionId: { type: String, required: true },
    eventId: { type: String, required: true },
    competitor: {
      type: Object as PropType<SportCompetitor>,
      required: false,
      default: undefined
    },
    alignbadge: {
      type: String,
      default: "left",
      required: false
    },
    isDraw: {
      type: Boolean,
      default: false
    },
    homeOrAwayTeam: {
      type: String as PropType<"home" | "away">,
      required: true,
      default: "home"
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    textWidth: {
      type: Number,
      default: 64,
      required: false
    },
    badgeAlignment: {
      type: Object as PropType<
        Partial<{
          left: number;
          right: number;
        }>
      >,
      required: false,
      default: undefined
    },
    stagger: {
      type: Number,
      default: 0
    },
    isLeft: {
      type: Boolean,
      default: false
    },
    disableBadges: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    provide("button-transition-stagger", props.stagger);
    const { brand, adTemplate, getButtonStyle, adTemplateBranding } =
      injectBrand() || ({} as BrandContext);

    const showTeamBadge = injectUseTeamBadges();
    const showTeamJerseys = injectUseTeamJerseys();
    const teamColorsContext = injectTeamColors();
    const carouselContext = injectCarouselContext();
    const templateSizeContext = injectTemplateSize();

    const badgeOrJersey = computed(() => {
      if (props.isDraw) return false;
      return teamColorsContext.teamIconsAvailable.value && !props.disableBadges;
    });

    const showWinnings = useShowWinnings();
    const buttonId = computed(() => adTemplate.value?.market?.oddsButton || "");

    const buttonStyle = computed(() => {
      return getButtonStyle(buttonId.value);
    });

    const teamColors = computed(
      () => teamColorsContext.colors.value[props.homeOrAwayTeam]
    );

    const competitorName = computed(
      () =>
        (carouselContext.value.isCarousel &&
        templateSizeContext?.size.value.width !== 980
          ? props.competitor?.name.micro
          : props.competitor?.name.short) || ""
    );

    const backgroundColor = computed(() => {
      if (
        !props.isDraw &&
        teamColorsContext.teamColorsAvailable.value &&
        teamColors.value?.banner.background
      ) {
        return teamColors.value?.banner.background;
      }

      return buttonStyle.value.backgroundColor;
    });

    const textColor = computed(() => {
      if (
        !props.isDraw &&
        teamColorsContext.teamColorsAvailable.value &&
        teamColors.value?.banner.text
      ) {
        return teamColors.value?.banner.text;
      }

      return undefined;
    });

    const borderRadius = computed(() => {
      const { borderRadius } = buttonStyle.value;
      if (!showTeamBadge.value || !showTeamJerseys.value) {
        return borderRadius;
      } else {
        return props.alignbadge === "left"
          ? `0px ${borderRadius}px ${borderRadius}px 0px`
          : `${borderRadius}px 0px 0px ${borderRadius}px`;
      }
    });

    const styleComputed = computed(() => ({
      showTeamBadges: showTeamBadge.value,
      showTeamJerseys: showTeamJerseys.value,
      badgesOrJersey: badgeOrJersey.value,
      buttonBackground: backgroundColor.value,
      adTemplateBranding: adTemplateBranding.value,
      alignment: props.alignbadge,
      borderRadius: borderRadius.value,
      textWidth: props.textWidth,
      rightBagdeAlignment: props.badgeAlignment?.right
        ? props.badgeAlignment.right
        : 0,
      leftBadgeAlignment: props.badgeAlignment?.left ? props.badgeAlignment : 0,
      badgeBorderWidth: teamColorsContext.teamColorsAvailable.value
        ? "0px !important"
        : "2px !important"
    }));

    const classes = useStyles(styleComputed);
    const option = composeBetOption(props.optionId) as ComputedRef<Option>;

    const { localisedWinnings } = usePotentialWinnings(
      option.value.odds || [0, 0]
    );

    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      {
        ...props,
        cardNumber: cardNumber,
        marketId: option.value.marketID,
        optionName: option.value.name?.full
      },
      "Odds Button"
    );

    const handleButtonClick = () => {
      if (option.value.suspended || !props.optionId) return;
      handleClick();
    };

    const { targetEl } = composePopoverForm("Market Button", [
      {
        type: "button",
        get: () => brand.value?.adTemplate.market.oddsButton || "",
        set: (value: string) => {
          if (brand.value?.adTemplate) {
            brand.value.adTemplate.market.oddsButton = value;
          }
        }
      }
    ]);

    const odds = computed(
      () =>
        (option &&
          formatOdds(
            option.value.odds,
            adTemplate.value?.market.oddsButtonTemplate ||
              oddsTypeEnum.FRACTIONAL_ODDS
          )) ||
        "-"
    );

    const label = computed(() =>
      option.value.label === "draw"
        ? getTranslation(window.navigator.language.toLowerCase()).ButtonTexts
            .Draw
        : option.value.label
    );

    const teamIconsAvailable = computed(
      () => teamColorsContext.teamIconsAvailable.value
    );

    return {
      competitorName,
      teamIconsAvailable,
      showTeamBadge,
      showTeamJerseys,
      badgeOrJersey,
      buttonId,
      primaryFont: computed(() => buttonStyle.value?.primaryFont as string),
      secondaryFont: computed(() => buttonStyle.value?.secondaryFont as string),
      url: option?.value.betslipUrl,
      meta: option?.value.meta,
      odds,
      label,
      backgroundColor,
      classes,
      option,
      targetEl,
      handleButtonClick,
      buttonStyle,
      winnings: localisedWinnings,
      showWinnings,
      textColor
    };
  }
});
</script>

<style></style>
